import { InitOptions, Theme } from './schemas'

export const GIFT_VALUE = 30

const DEFAULT_ENTRYPOINT: Record<string, string> = {
  production: 'https://www.gonift.com',
  staging: 'https://staging.gonift.com',
  development: 'http://localhost:3000',
}

const REGION_TO_COUNTRY_CODE: Record<string, string> = {
  us: 'US',
  uk: 'GB',
}

const DEFAULT_REGION_CODES = new Set(['US', 'USA', 'CA', 'CAN'])

export class ConfigManager {
  private static instance: ConfigManager
  private config: InitOptions | null = null
  private theme: Theme | null = null
  private activationLink: string | null = null
  private partnerName: string | null = null
  private referralCode: string | null = null
  private skipOffer: boolean = false
  private showClose: boolean = false
  private resolvedApiUrl: string | null = null

  private constructor() {}

  static getInstance(): ConfigManager {
    if (!ConfigManager.instance) {
      ConfigManager.instance = new ConfigManager()
    }
    return ConfigManager.instance
  }

  setConfig(config: InitOptions): void {
    this.config = config
  }

  getConfig(): InitOptions {
    if (!this.config) {
      throw new Error('SDK not initialized. Call NiftWebSDK.init() first.')
    }
    return this.config
  }

  isInitialized(): boolean {
    return this.config !== null
  }

  async resolveApiUrl(): Promise<void> {
    const config = this.getConfig()
    const environment = config.environment || 'production'
    const entrypoint = DEFAULT_ENTRYPOINT[environment] || DEFAULT_ENTRYPOINT.production

    if (config.apiEndpoint) {
      this.resolvedApiUrl = config.apiEndpoint
      return
    }

    const countryCode =
      (config.countryCode?.trim().toUpperCase() || null) ??
      REGION_TO_COUNTRY_CODE[config.region || ''] ??
      'US'

    if (environment === 'development') {
      this.resolvedApiUrl = entrypoint
      return
    }

    if (DEFAULT_REGION_CODES.has(countryCode)) {
      this.resolvedApiUrl = entrypoint
      return
    }

    try {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 5000)

      const response = await fetch(`${entrypoint}/global/${countryCode}/regional_endpoints`, {
        signal: controller.signal,
      })
      clearTimeout(timeout)

      if (response.ok) {
        const data = await response.json()
        this.resolvedApiUrl = data.api_url?.replace(/\/$/, '') || entrypoint
      } else {
        this.resolvedApiUrl = entrypoint
      }
    } catch {
      this.resolvedApiUrl = entrypoint
    }
  }

  getApiUrl(): string {
    if (this.resolvedApiUrl) {
      return this.resolvedApiUrl
    }

    const config = this.getConfig()
    if (config.apiEndpoint) {
      return config.apiEndpoint
    }
    const environment = config.environment || 'production'
    return DEFAULT_ENTRYPOINT[environment] || DEFAULT_ENTRYPOINT.production
  }

  getClientId(): string {
    return this.getConfig().clientId
  }

  getCustomer() {
    return this.getConfig().customer
  }

  getCode(): string {
    return this.getConfig().code
  }

  getPartnerName(): string | undefined {
    return this.getConfig().partnerName
  }

  setTheme(theme: Theme): void {
    this.theme = theme
  }

  getTheme(): Theme | null {
    return this.theme
  }

  setActivationLink(link: string): void {
    this.activationLink = link
  }

  getActivationLink(): string | null {
    return this.activationLink
  }

  updateCustomerFromActivationLink(activationLink: string): void {
    try {
      const url = new URL(activationLink, this.getApiUrl())
      const email = url.searchParams.get('email')
      if (email && this.config?.customer) {
        this.config.customer.email = email
      }
    } catch {
      // if URL parsing fails, keep the original customer data
    }
  }

  setPartnerName(name: string): void {
    this.partnerName = name
  }

  getStoredPartnerName(): string | null {
    return this.partnerName
  }

  setReferralCode(code: string): void {
    this.referralCode = code
  }

  getReferralCode(): string | null {
    return this.referralCode
  }

  setSkipOffer(skip: boolean): void {
    this.skipOffer = skip
  }

  getSkipOffer(): boolean {
    return this.skipOffer
  }

  setShowClose(show: boolean): void {
    this.showClose = show
  }

  getShowClose(): boolean {
    return this.showClose
  }

  reset(): void {
    this.config = null
    this.theme = null
    this.activationLink = null
    this.partnerName = null
    this.referralCode = null
    this.skipOffer = false
    this.showClose = false
    this.resolvedApiUrl = null
  }
}
